import {
  takeLatest,
  put,
  call,
  select,
  take,
  all,
  race,
  takeEvery,
} from 'redux-saga/effects';
import { LOCATION_CHANGE } from 'connected-react-router';

import { getEntityId, getEntityType } from './apiHelpers';
import {
  isAppReadySelector,
  bootstrapCompleted,
  baseApiUrlSelector,
} from '../bootstrapSlice';
import { flattenMultipartFormDto } from '../../utilities/mapperFunctions';

function runWhenBootstrapped(fn) {
  return function* (action) {
    const isReady = yield select(isAppReadySelector);
    if (!isReady) {
      yield take(bootstrapCompleted.type);
    }
    yield call(fn, action);
  };
}

export function* takeLatestAfterBootstrap(actionType, fn) {
  yield takeLatest(actionType, runWhenBootstrapped(fn));
}

export function* takeEveryAfterBootstrap(actionType, fn) {
  yield takeEvery(actionType, runWhenBootstrapped(fn));
}

export function* takeAfterBootstrap(actionType, fn) {
  yield take(actionType, runWhenBootstrapped(fn));
}

export function* callAndAwaitApi(initAction) {
  const action = yield put(initAction);
  return yield awaitApiCall(action);
}

export function awaitApiCall(initAction) {
  const actionPrefix = initAction.type.replace(/init$/, '');
  const errorActionType = actionPrefix + 'error';
  const successActionType = actionPrefix + 'success';
  return race({
    error: take(
      (action) =>
        action.type === errorActionType &&
        action.payload.initAction === initAction,
    ),
    success: take(
      (action) =>
        action.type === successActionType &&
        action.payload.initAction === initAction,
    ),
  });
}

export function* captureLocationUpdate(locationMatcherFn, fn) {
  yield takeLatestAfterBootstrap(
    (action) =>
      action.type === LOCATION_CHANGE &&
      locationMatcherFn(action.payload.location),
    fn,
  );
}

export function* awaitApiCalls(initActions) {
  const allReponses = yield all(initActions.slice().map(awaitApiCall));
  return {
    allReponses,
    isSuccess: function () {
      return this.allReponses.every((r) => !!r.success);
    },
    getReponse: function (initAction) {
      const response = this.allReponses.find(
        (r) =>
          (r.success && r.success.payload.initAction === initAction) ||
          (r.error && r.error.payload.initAction),
      );
      return response.success || response.error;
    },
  };
}

export function readStream(streamReader) {
  let result;

  return new Promise((resolve) => {
    streamReader.read().then(function processText({ done, value }) {
      if (done) {
        return resolve(result);
      }

      if (!result) result = value;
      else {
        const combinedResult = new Int8Array(result.length + value.length);
        combinedResult.set(result);
        combinedResult.set(value, result.length);
        result = combinedResult;
      }
      return new Promise((resolve) => {
        resolve(streamReader.read().then(processText));
      });
    });
  });
}

export function generateGetAllApiCall(
  ConnectorClass,
  genRequestFunc,
  ignoreResponse = false,
) {
  return function* (action) {
    const actionPrefix = action.type.replace(/init$/, '');
    let request = null;
    try {
      yield put({
        type: actionPrefix + 'request',
        payload: {
          initAction: action,
        },
      });

      const url = yield select(baseApiUrlSelector);
      const connector = new ConnectorClass(url);
      request = yield call(genRequestFunc, connector, action);
      if (request.status === '401' || request.status === '403') {
        yield put({
          type: actionPrefix + 'error',
          payload: {
            error: 'Authentication error ' + request.status,
            initAction: action,
            statusCode: request && request.status,
          },
        });
      } else {
        let jsonData = null;
        if (!ignoreResponse) {
          jsonData = yield call([request, request.json]);
        }
        yield put({
          type: request.ok ? actionPrefix + 'success' : actionPrefix + 'error',
          payload: {
            requestData: jsonData,
            request: request,
            initAction: action,
            statusCode: request && request.status,
          },
        });
      }
    } catch (error) {
      yield put({
        type: actionPrefix + 'error',
        payload: {
          error,
          initAction: action,
          statusCode: request && request.status,
        },
      });
    }
  };
}

export function generateCreateApiCall(ConnectorClass, genRequestFunc) {
  return function* (action) {
    const actionPrefix = action.type.replace(/init$/, '');
    let request = null;
    try {
      yield put({
        type: actionPrefix + 'request',
        payload: {
          initAction: action,
        },
      });
      const url = yield select(baseApiUrlSelector);
      const connector = new ConnectorClass(url);
      request = yield call(genRequestFunc, connector, action);
      const jsonData = yield call([request, request.json]);
      yield put({
        type: request.ok ? actionPrefix + 'success' : actionPrefix + 'error',
        payload: {
          requestData: jsonData,
          request: request,
          initAction: action,
          statusCode: request && request.status,
        },
      });
    } catch (error) {
      yield put({
        type: actionPrefix + 'error',
        payload: {
          error,
          initAction: action,
          statusCode: request && request.status,
        },
      });
    }
  };
}

export function generateMultipartFormApiCall(parrentName, excludedProperties) {
  return (ConnectorClass, genRequestFunc) => {
    return function* (action) {
      const actionPrefix = action.type.replace(/init$/, '');
      let request = null;
      try {
        const { dto } = action.payload;
        let requestData = {
          formData: new FormData(),
        };
        const flattenedDto = flattenMultipartFormDto(
          dto,
          parrentName,
          excludedProperties,
        );
        const keys = Object.keys(flattenedDto);
        keys.forEach((key) =>
          requestData.formData.append(key, flattenedDto[key]),
        );

        yield put({
          type: actionPrefix + 'request',
          payload: {
            initAction: action,
            requestData,
          },
        });
        const url = yield select(baseApiUrlSelector);
        const connector = new ConnectorClass(url);
        request = yield call(genRequestFunc, connector, action, requestData);
        const jsonData = yield call([request, request.json]);
        yield put({
          type: request.ok ? actionPrefix + 'success' : actionPrefix + 'error',
          payload: {
            requestData: jsonData,
            request: request,
            initAction: action,
            statusCode: request && request.status,
          },
        });
      } catch (error) {
        yield put({
          type: actionPrefix + 'error',
          payload: {
            error,
            initAction: action,
            statusCode: request && request.status,
          },
        });
      }
    };
  };
}

export function generateUpdateApiCall(ConnectorClass, genRequestFunc) {
  return function* (action) {
    const actionPrefix = action.type.replace(/init$/, '');
    let request = null;
    try {
      const { model, changes } = action.payload;
      let requestData = null;
      if (model) {
        requestData = {
          data: Object.assign({}, changes, {
            id: getEntityId(model),
            type: getEntityType(model),
          }),
        };
      } else {
        requestData = {
          data: changes,
        };
      }

      yield put({
        type: actionPrefix + 'request',
        payload: {
          initAction: action,
          requestData,
        },
      });
      const url = yield select(baseApiUrlSelector);
      const connector = new ConnectorClass(url);
      request = yield call(genRequestFunc, connector, action, requestData);
      const jsonData = yield call([request, request.json]);
      yield put({
        type: request.ok ? actionPrefix + 'success' : actionPrefix + 'error',
        payload: {
          requestData: jsonData,
          request: request,
          initAction: action,
          statusCode: request && request.status,
        },
      });
    } catch (error) {
      yield put({
        type: actionPrefix + 'error',
        payload: {
          error,
          initAction: action,
          statusCode: request && request.status,
        },
      });
    }
  };
}
